/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import {
    AlertDialog,
    AlertDialogContent,
    Badge,
    Button,
    Card,
    Checkbox,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    MoreButton,
    Pagination,
    SearchInput,
    Table,
    Tag,
} from "@jhool-io/fe-components";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useSearchParams } from "react-router-dom";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import {
    IInternalUser,
    InternalUserStatus,
    UserRole,
} from "../../types/user-management.types";

import useToast from "../../../../hooks/useToast";
import { useFetchUserDetails } from "../../../../hooks/queries/user";
import {
    useChangeUserStatus,
    useDeleteUsers,
    useResetPassword,
    useResetUserMfa,
} from "../../hooks/user-management.mutations";
import { useFetchUsersList } from "../../hooks/user-management.queries";
import { useDebounce } from "../../../../hooks/helpers";
import { APP_COLORS, LISTS_DEFAULT_LIMIT } from "../../../../utils/constants";
import {
    displayNameInRightFormat,
    getTotalAmountOfTableRowsSelected,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    truncateString,
} from "../../../../utils/helpers";
import ModifyUserRoleForm from "../ModifyUserRoleForm/ModifyUserRoleForm";
import ListState from "../../../../components/ListState/ListState";
import ArrowDownIcon from "../../../../components/Icons/ArrowDownIcon";
import MobileListItem from "../../../../components/MobileListItem/MobileListItem";
import AddUserForm from "../AddUserForm/AddUserForm";
import EditUserNameForm from "../EditUserNameForm/EditUserNameForm";
import Loader from "../../../../shared-ui/Loader/Loader";
import TableFiltersDisplay from "../../../../shared-ui/TableFiltersDisplay/TableFiltersDisplay";
import { useAppSelector } from "../../../../hooks/useRedux";
import {
    canEditUserName,
    canManageUserStatus,
    canModifyUserRole,
    canModifyUserRoles,
    canResetUserMfa,
    isProtectedSupportUser,
    roleLabels,
} from "../../utils/roles";

type DialogTypes =
    | "add-user"
    | "delete-user"
    | "deactivate-user"
    | "modify-user"
    | "activate-user"
    | "reset-password"
    | "reset-mfa"
    | "edit-name";

export default function InternalUsersList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [rowSelection, setRowSelection] = React.useState({});
    const [dialogInView, setDialogInView] = React.useState<DialogTypes | null>(
        null
    );
    const [selectedUserId, setSelectedUserId] = React.useState<string | null>(
        null
    );
    // Selected users id for mobile
    const [selectedUsersId, setSelectedUsersId] = React.useState<string[]>([]);
    const [getSelectedData, setGetSelectedData] = React.useState<
        Row<IInternalUser>[]
    >([]);
    const [sortConfig, setSortConfig] = React.useState<{
        key: string;
        direction: string;
    }>({
        key: "",
        direction: "",
    });

    const pageFilter = Number(searchParams.get("page")) || 1;
    const searchFilter = searchParams.get("search") || "";
    const roleFilter = searchParams.getAll("role[]") || [];
    const statusFilter =
        searchParams.get("status") === null
            ? ""
            : searchParams.get("status") || "active";
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;

    const debouncedSearchFilter = useDebounce(searchFilter);

    const userIdsFromSelectedData =
        getSelectedData && getSelectedData.map((item) => item.original.user_id);

    const queryClient = useQueryClient();

    const loggedInUser = useFetchUserDetails();
    const { practice } = useAppSelector((state) => state.userPractice);
    const actorRoles = practice?.roles ?? [];
    const isSuperAdmin = actorRoles.includes(UserRole.SUPER_ADMIN);
    const isSupportAdmin =
        actorRoles.includes(UserRole.SUPPORT_ADMIN) && !isSuperAdmin;
    const canModifyRoles = canModifyUserRoles(actorRoles);
    const deleteUsers = useDeleteUsers();
    const changeUserStatus = useChangeUserStatus();
    const resetPassword = useResetPassword();
    const resetMfa = useResetUserMfa();

    const { toast } = useToast();

    const sortableColumns = ["user_name", "email", "roles"];

    const modifyRolesIsMutating = useIsMutating(["modify-roles"]);
    const addUserIsMutating = useIsMutating(["useAddUsers"]);
    const resetPasswordIsMutating = useIsMutating(["reset-password"]);
    const updateStatusIsMutating = useIsMutating(["update-status"]);
    const deleteUserIsMutating = useIsMutating(["delete-users"]);
    const resetMfaIsMutating = useIsMutating(["reset-user-mfa"]);
    const editUserNameIsMutating = useIsMutating(["edit-user-name"]);

    const handleConfigAttribute = () => {
        if (sortableColumns.includes(sortConfig.key)) {
            return sortConfig.key;
        }

        return undefined;
    };

    const { data, isLoading, isSuccess, error } = useFetchUsersList({
        page: pageFilter,
        limit: limitFilter,
        status: statusFilter,
        roles: roleFilter,
        search_string: debouncedSearchFilter,
        sort_attribute:
            searchParams.get("sort_attr") || handleConfigAttribute(),
        sort_order: searchParams.get("sort_order") || sortConfig.direction,
    });

    // Details for a single user web
    const selectedUserDetails = data?.data.find((user) => {
        return user.user_id === selectedUserId;
    });

    // Array of details of the selected users(web)
    const selectedUsersDetailsWeb =
        userIdsFromSelectedData &&
        userIdsFromSelectedData.map((userId: string) =>
            data?.data.find((user) => user.user_id === userId)
        );

    // Array of details of the selected users(mobile)
    const selectedUsersDetailsMobile =
        selectedUsersId &&
        selectedUsersId.map((userId: string) =>
            data?.data.find((user) => user.user_id === userId)
        );

    const detailsArrayToUse =
        selectedUsersId.length > 0
            ? selectedUsersDetailsMobile
            : selectedUsersDetailsWeb;

    const handleSearchStringChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (e.target.value === "") searchParams.delete("search");
        else searchParams.set("search", e.target.value);

        setSearchParams(searchParams);
    };

    const handleStatusSelectFilterChange = (value: string) => {
        if (value === "") searchParams.delete("status");
        else searchParams.set("status", value);

        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleRoleSelectFilterChange = (role: string) => {
        if (roleFilter.includes(role)) {
            searchParams.delete("role[]", role);
        } else {
            searchParams.append("role[]", role);
        }
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handlePageChange = (page: number) => {
        searchParams.set("page", String(page));
        setSearchParams(searchParams);
    };

    const handleLimitChange = (limit: number) => {
        searchParams.set("limit", String(limit));
        setSearchParams(searchParams);
    };

    const handleSetDialogInView = (
        dialog: DialogTypes | null,
        id: string | null
    ) => {
        setDialogInView(dialog);
        setSelectedUserId(id);
    };

    const handleCloseDialog = () => {
        setDialogInView(null);
    };

    const handleResetLocalStates = () => {
        if (selectedUserId) setSelectedUserId(null);
        if (rowSelection && Object.keys(rowSelection).length > 0)
            setRowSelection({});
        if (selectedUsersId.length > 0) setSelectedUsersId([]);
    };

    // Toggles user selection for mobile
    const toggleUserSelection = (userId: string) => {
        if (selectedUsersId.includes(userId)) {
            setSelectedUsersId(selectedUsersId.filter((id) => id !== userId));
        } else {
            setSelectedUsersId([...selectedUsersId, userId]);
        }
    };

    const getStatusDisplayForMobile = (status: string) => {
        let bgColor: string;
        let textColor: string;

        switch (status) {
            case InternalUserStatus.ACTIVE:
                textColor = "#00563E";
                bgColor = "rgba(204, 250, 233, 0.50)";
                break;
            case InternalUserStatus.DEACTIVATED:
                textColor = "#981F41";
                bgColor = "rgba(251, 199, 198, 0.50)";
                break;
            default:
                bgColor = "rgba(245, 245, 245, 0.50)";
                textColor = APP_COLORS.COLOR_BLACK;
                break;
        }

        return (
            <Tag
                textColor={textColor}
                bgColor={bgColor}
                title={makeStringFirstLetterCapital(status)}
                className="capitalize w-auto h-auto text-nowrap px-8 py-4 rounded-r4"
            />
        );
    };

    const handleDeleteUsers = () => {
        deleteUsers.mutate(
            userIdsFromSelectedData && userIdsFromSelectedData.length > 0
                ? userIdsFromSelectedData
                : [selectedUserId || ""],
            {
                onSuccess(res) {
                    queryClient.invalidateQueries({ queryKey: [`users`] });
                    toast({
                        mode: "success",
                        message: res.message || "User(s) deleted successfully",
                    });
                    handleResetLocalStates();
                    handleCloseDialog();
                },
                onError(err) {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not delete user(s), please try again",
                    });
                },
            }
        );
    };

    const handleChangeStatus = () => {
        const singlePayload = {
            user_ids: [selectedUserDetails?.user_id as string],
            status:
                selectedUserDetails?.status === InternalUserStatus.ACTIVE
                    ? InternalUserStatus.DEACTIVATED
                    : InternalUserStatus.ACTIVE,
        };

        const multiPayload = {
            user_ids:
                userIdsFromSelectedData.length === 0
                    ? selectedUsersId
                    : userIdsFromSelectedData,
            status:
                dialogInView === "activate-user"
                    ? InternalUserStatus.ACTIVE
                    : InternalUserStatus.DEACTIVATED,
        };
        changeUserStatus.mutate(
            detailsArrayToUse.length !== 0 ? multiPayload : singlePayload,
            {
                onSuccess(res) {
                    queryClient.invalidateQueries({ queryKey: [`users`] });
                    toast({
                        mode: "success",
                        message:
                            res.message || "User status updated successfully",
                    });
                    handleResetLocalStates();
                    handleCloseDialog();
                },
                onError(err) {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not update status, please try again",
                    });
                },
            }
        );
    };

    const handleResetUserPassword = () => {
        const emails = detailsArrayToUse.map((user) => user?.email);
        resetPassword.mutate(
            detailsArrayToUse && detailsArrayToUse?.length !== 0
                ? (emails as string[])
                : [selectedUserDetails?.email as string],
            {
                onSuccess() {
                    toast({
                        mode: "success",
                        message:
                            "A password reset link has been sent to the user",
                    });
                    handleResetLocalStates();
                    handleCloseDialog();
                },
                onError() {
                    toast({
                        mode: "error",
                        message: "User password reset failed",
                    });
                },
            }
        );
    };

    const handleResetUserMfa = () => {
        if (!selectedUserDetails) {
            return;
        }
        resetMfa.mutate(selectedUserDetails.user_id, {
            onSuccess(res) {
                toast({
                    mode: "success",
                    message: res.message,
                });
                handleCloseDialog();
            },
            onError(err) {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ??
                        "Could not reset authenticator enrollment",
                });
            },
        });
    };

    const canModifyTarget = (roles: string[]) =>
        canModifyRoles &&
        (isSuperAdmin || (!isSupportAdmin && !isProtectedSupportUser(roles)));
    const selectedUsers = detailsArrayToUse.filter(
        (user): user is IInternalUser => Boolean(user)
    );
    const canModifySelected =
        selectedUsers.length > 0 &&
        selectedUsers.every((user) => canModifyTarget(user.roles));
    const canModifySelectedRoles =
        selectedUsers.length > 0 &&
        selectedUsers.every((user) =>
            canModifyUserRole(actorRoles, user.roles)
        );
    const canManageSelectedStatus =
        selectedUsers.length > 0 &&
        selectedUsers.every((user) =>
            canManageUserStatus(actorRoles, user.roles)
        );

    const columns: ColumnDef<IInternalUser>[] = [
        {
            id: "select",
            header: ({ table }) => (
                <Checkbox
                    handleChange={table.getToggleAllRowsSelectedHandler()}
                    indeterminate={table.getIsSomeRowsSelected()}
                    name="row"
                    value="row"
                    isChecked={table.getIsAllRowsSelected()}
                />
            ),
            cell: ({ row }) => (
                <Checkbox
                    handleChange={row.getToggleSelectedHandler()}
                    indeterminate={row.getIsSomeSelected()}
                    name="row"
                    value="row"
                    isChecked={row.getIsSelected()}
                />
            ),
        },
        {
            accessorKey: "user_name",
            header: "USER NAME",
            cell: ({ row }) =>
                displayNameInRightFormat({
                    firstName: row.original.first_name,
                    lastName: row.original.last_name,
                }),
        },
        {
            accessorKey: "email",
            header: "EMAIL",
        },
        {
            accessorKey: "roles",
            header: "ROLES",
            cell: ({ row }) => {
                const roleName = row.original.roles
                    .map((role) => roleLabels[role as UserRole] ?? role)
                    .join(", ");
                return <span className="capitalize">{roleName}</span>;
            },
        },
        {
            accessorKey: "status",
            header: "STATUS",
            cell: ({ row }) =>
                row.original.status === InternalUserStatus.ACTIVE ? (
                    <Badge
                        type="success"
                        content={removeEnumUnderscore(
                            makeStringFirstLetterCapital(row.original.status)
                        )}
                    />
                ) : (
                    <Badge
                        type="danger"
                        content={removeEnumUnderscore(
                            makeStringFirstLetterCapital(row.original.status)
                        )}
                    />
                ),
        },
        {
            accessorKey: "user_id",
            header: "",
            cell: ({ row }) => {
                return (
                    loggedInUser.data?.user_id !== row.original.user_id && (
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <MoreButton />
                            </DropdownTrigger>
                            <DropdownContent width="auto" align="end">
                                {canModifyUserRole(
                                    actorRoles,
                                    row.original.roles
                                ) && (
                                    <DropdownItem
                                        onClick={() =>
                                            handleSetDialogInView(
                                                "modify-user",
                                                row.original.user_id
                                            )
                                        }
                                    >
                                        Modify user role
                                    </DropdownItem>
                                )}
                                {canEditUserName(
                                    actorRoles,
                                    row.original.roles
                                ) &&
                                    !row.original.is_intake_managed &&
                                    row.original.is_google_user === false && (
                                        <DropdownItem
                                            onClick={() =>
                                                handleSetDialogInView(
                                                    "edit-name",
                                                    row.original.user_id
                                                )
                                            }
                                        >
                                            Edit name
                                        </DropdownItem>
                                    )}
                                {canModifyUserRole(
                                    actorRoles,
                                    row.original.roles
                                ) && (
                                    <DropdownItem
                                        onClick={() =>
                                            handleSetDialogInView(
                                                "reset-password",
                                                row.original.user_id
                                            )
                                        }
                                    >
                                        Reset user password
                                    </DropdownItem>
                                )}
                                {canResetUserMfa(
                                    actorRoles,
                                    row.original.roles
                                ) && (
                                    <DropdownItem
                                        onClick={() =>
                                            handleSetDialogInView(
                                                "reset-mfa",
                                                row.original.user_id
                                            )
                                        }
                                    >
                                        Reset authenticator
                                    </DropdownItem>
                                )}
                                {canManageUserStatus(
                                    actorRoles,
                                    row.original.roles
                                ) && (
                                    <DropdownItem
                                        onClick={
                                            row.original.status ===
                                            InternalUserStatus.ACTIVE
                                                ? () =>
                                                      handleSetDialogInView(
                                                          "deactivate-user",
                                                          row.original.user_id
                                                      )
                                                : () =>
                                                      handleSetDialogInView(
                                                          "activate-user",
                                                          row.original.user_id
                                                      )
                                        }
                                    >
                                        {row.original.status ===
                                        InternalUserStatus.ACTIVE
                                            ? "Deactivate user"
                                            : "Activate user"}
                                    </DropdownItem>
                                )}
                                {canModifyTarget(row.original.roles) && (
                                    <DropdownItem
                                        className="text-danger"
                                        onClick={() =>
                                            handleSetDialogInView(
                                                "delete-user",
                                                row.original.user_id
                                            )
                                        }
                                    >
                                        Delete user
                                    </DropdownItem>
                                )}
                            </DropdownContent>
                        </Dropdown>
                    )
                );
            },
        },
    ];

    return (
        <>
            <Dialog open={dialogInView === "add-user"}>
                <DialogContent
                    handleCloseDialog={handleCloseDialog}
                    variant="center"
                    title="Create internal user"
                    saveText="Create internal user"
                    submitBtnFormValue="add-user"
                    isSubmitBtnDisabled={addUserIsMutating > 0}
                    isCancelBtnDisabled={addUserIsMutating > 0}
                >
                    <AddUserForm
                        onFormSubmit={() => {
                            handleCloseDialog();
                            handleResetLocalStates();
                        }}
                        selectedActionType="invite_user"
                    />
                </DialogContent>
            </Dialog>
            <Dialog open={canModifyRoles && dialogInView === "modify-user"}>
                <DialogContent
                    handleCloseDialog={handleCloseDialog}
                    variant="center"
                    title="Modify user role"
                    saveText="Modify role"
                    submitBtnFormValue="modify_user_role"
                    isSubmitBtnDisabled={modifyRolesIsMutating > 0}
                    isCancelBtnDisabled={modifyRolesIsMutating > 0}
                >
                    <ModifyUserRoleForm
                        onFormSubmit={() => {
                            handleCloseDialog();
                            handleResetLocalStates();
                        }}
                        defaultRoles={
                            userIdsFromSelectedData.length === 0
                                ? selectedUserDetails?.roles
                                : []
                        }
                        defaultUsers={
                            selectedUserDetails
                                ? [selectedUserDetails.user_id]
                                : detailsArrayToUse
                                      .filter(Boolean)
                                      .map((user) => user?.user_id as string)
                        }
                    />
                </DialogContent>
            </Dialog>
            {selectedUserDetails && (
                <Dialog open={dialogInView === "edit-name"}>
                    <DialogContent
                        handleCloseDialog={handleCloseDialog}
                        variant="center"
                        title="Edit user name"
                        saveText="Save name"
                        submitBtnFormValue="edit-user-name"
                        isSubmitBtnDisabled={editUserNameIsMutating > 0}
                        isCancelBtnDisabled={editUserNameIsMutating > 0}
                    >
                        <EditUserNameForm
                            user={selectedUserDetails}
                            onFormSubmit={() => {
                                handleCloseDialog();
                                handleResetLocalStates();
                            }}
                        />
                    </DialogContent>
                </Dialog>
            )}
            <AlertDialog open={dialogInView === "delete-user"}>
                <AlertDialogContent
                    title={
                        detailsArrayToUse.length === 0
                            ? `Are you sure you want to remove ${selectedUserDetails?.email}?`
                            : `Are you sure you want to remove these users:`
                    }
                    description={
                        detailsArrayToUse.length === 0 ? (
                            "Deleting this user means all of its data will be removed and this action can not be reversed"
                        ) : (
                            <>
                                <p>
                                    {detailsArrayToUse.map((user) => (
                                        <span key={user?.email}>
                                            {user?.email}
                                            <br />
                                        </span>
                                    ))}
                                </p>
                                <p>
                                    Deleting these users means all of their data
                                    will be removed and this action cannot be
                                    reversed.
                                </p>
                            </>
                        )
                    }
                    onCancelClick={handleCloseDialog}
                    saveText="Delete user"
                    onSaveClick={handleDeleteUsers}
                    isSaveBtnDisabled={deleteUserIsMutating > 0}
                    isCancelBtnDisabled={deleteUserIsMutating > 0}
                    isDeleting
                />
            </AlertDialog>
            <AlertDialog open={dialogInView === "deactivate-user"}>
                <AlertDialogContent
                    onCancelClick={handleCloseDialog}
                    isDeleting
                    title={
                        detailsArrayToUse.length === 0
                            ? `Are you sure you want to deactivate ${selectedUserDetails?.email}?`
                            : "Are you sure you want to deactivate these users:"
                    }
                    description={
                        detailsArrayToUse.length === 0 ? (
                            "This will restrict the user from using mantle"
                        ) : (
                            <>
                                <p>
                                    {detailsArrayToUse.map((user) => (
                                        <span key={user?.email}>
                                            {user?.email}
                                            <br />
                                        </span>
                                    ))}
                                </p>
                                <p>This will restrict them from using mantle</p>
                            </>
                        )
                    }
                    saveText="Deactivate user"
                    onSaveClick={handleChangeStatus}
                    isSaveBtnDisabled={updateStatusIsMutating > 0}
                    isCancelBtnDisabled={updateStatusIsMutating > 0}
                />
            </AlertDialog>
            <AlertDialog open={dialogInView === "activate-user"}>
                <AlertDialogContent
                    onCancelClick={handleCloseDialog}
                    title={
                        detailsArrayToUse.length === 0
                            ? `Are you sure you want to activate ${selectedUserDetails?.email}?`
                            : "Are you sure you want to activate these users:"
                    }
                    description={
                        detailsArrayToUse.length === 0 ? (
                            "This will give them access to use mantle"
                        ) : (
                            <>
                                <p>
                                    {detailsArrayToUse.map((user) => (
                                        <span key={user?.email}>
                                            {user?.email}
                                            <br />
                                        </span>
                                    ))}
                                </p>
                                <p>This will give them access to use mantle</p>
                            </>
                        )
                    }
                    saveText="Activate user"
                    onSaveClick={handleChangeStatus}
                    isSaveBtnDisabled={updateStatusIsMutating > 0}
                    isCancelBtnDisabled={updateStatusIsMutating > 0}
                />
            </AlertDialog>
            <AlertDialog open={dialogInView === "reset-password"}>
                <AlertDialogContent
                    onCancelClick={handleCloseDialog}
                    title={
                        detailsArrayToUse.length === 0
                            ? `Are you sure you want to reset password for ${selectedUserDetails?.email}?`
                            : " Are you sure you want to reset password for these users:"
                    }
                    description={
                        detailsArrayToUse.length !== 0 && (
                            <p>
                                {detailsArrayToUse.map((user) => (
                                    <span key={user?.email}>
                                        {user?.email}
                                        <br />
                                    </span>
                                ))}
                            </p>
                        )
                    }
                    saveText="Reset Password"
                    onSaveClick={handleResetUserPassword}
                    isSaveBtnDisabled={resetPasswordIsMutating > 0}
                    isCancelBtnDisabled={resetPasswordIsMutating > 0}
                />
            </AlertDialog>
            <AlertDialog open={dialogInView === "reset-mfa"}>
                <AlertDialogContent
                    onCancelClick={handleCloseDialog}
                    title="Reset authenticator enrollment?"
                    description="The user will be signed out and must set up an authenticator again."
                    saveText="Reset authenticator"
                    onSaveClick={handleResetUserMfa}
                    isSaveBtnDisabled={resetMfaIsMutating > 0}
                    isCancelBtnDisabled={resetMfaIsMutating > 0}
                />
            </AlertDialog>
            <div>
                <Card classNames="flex-col flex gap-[--spacing-items] mb-[--spacing-header]">
                    <SearchInput
                        placeholder="Search by name, email"
                        defaultValue={searchFilter}
                        onChange={handleSearchStringChange}
                        containerClass="w-[400px]"
                    />
                    <div className="flex flex-wrap gap-[--spacing-items] justify-between items-center">
                        <div className="flex items-center flex-wrap gap-[--spacing-items]">
                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton>Role</FilterButton>
                                </DropdownTrigger>
                                <DropdownContent width="auto">
                                    {Object.values(UserRole)
                                        .filter(
                                            (role) =>
                                                role !== UserRole.USER_SUPPORT
                                        )
                                        .map((role) => (
                                            <DropdownItem
                                                key={role}
                                                onClick={() =>
                                                    handleRoleSelectFilterChange(
                                                        role
                                                    )
                                                }
                                                isMulti
                                                itemValue={role}
                                                values={roleFilter}
                                            >
                                                {roleLabels[role]}
                                            </DropdownItem>
                                        ))}
                                </DropdownContent>
                            </Dropdown>
                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton
                                        className={
                                            statusFilter
                                                ? "capitalize"
                                                : "normal-case"
                                        }
                                    >
                                        {statusFilter
                                            ? truncateString(
                                                  removeEnumUnderscore(
                                                      statusFilter
                                                  )
                                              )
                                            : "Status"}
                                    </FilterButton>
                                </DropdownTrigger>
                                <DropdownContent width="auto">
                                    <DropdownItem
                                        isSelected={!statusFilter}
                                        onClick={() =>
                                            handleStatusSelectFilterChange("")
                                        }
                                    >
                                        All
                                    </DropdownItem>
                                    {Object.values(InternalUserStatus).map(
                                        (status) => (
                                            <DropdownItem
                                                isSelected={
                                                    statusFilter === status
                                                }
                                                key={status}
                                                onClick={() =>
                                                    handleStatusSelectFilterChange(
                                                        status
                                                    )
                                                }
                                            >
                                                {removeEnumUnderscore(status)}
                                            </DropdownItem>
                                        )
                                    )}
                                </DropdownContent>
                            </Dropdown>
                        </div>
                        <Button
                            aria-label="Invite User"
                            onClick={() => setDialogInView("add-user")}
                        >
                            Create Internal User
                        </Button>
                    </div>
                    <TableFiltersDisplay
                        appliedFilters={[
                            {
                                key: "role[]",
                                values: roleFilter,
                            },
                        ]}
                        wrapperClass="mt-[--spacing-items]"
                    />
                </Card>
                {isLoading && <Loader />}
                {error && (
                    <ListState
                        isError
                        stateHelperText="Try reloading this page to solve this issue"
                        errorMsg={
                            error?.response?.data.message ||
                            `Cannot display users list at this time please try again later`
                        }
                    />
                )}
                {data && isSuccess && data.data.length === 0 && (
                    <ListState
                        stateHelperText="Users list will appear here once users are added"
                        isError={false}
                        emptyMessage="No users added yet"
                        emptyBtnProps={{
                            showButton: true,
                            buttonText: "Create Internal User",
                            onButtonClick: () => setDialogInView("add-user"),
                        }}
                    />
                )}
                {isSuccess && data && data.data.length > 0 && (
                    <div>
                        {getTotalAmountOfTableRowsSelected(rowSelection)
                            .amount > 0 && (
                            <div className="flex items-center mb-[--spacing-items] gap-[--spacing-items] flex-wrap">
                                <div className="font-medium text-base">
                                    {
                                        getTotalAmountOfTableRowsSelected(
                                            rowSelection
                                        ).amountString
                                    }
                                    :
                                </div>
                                {canModifySelectedRoles && (
                                    <Button
                                        variant="secondary"
                                        aria-label="actions"
                                        onClick={() =>
                                            setDialogInView("modify-user")
                                        }
                                    >
                                        Modify role
                                    </Button>
                                )}
                                {canManageSelectedStatus &&
                                    detailsArrayToUse.every(
                                        (user) =>
                                            user?.status ===
                                                detailsArrayToUse[0]?.status ||
                                            ""
                                    ) && (
                                        <Button
                                            variant="secondary"
                                            aria-label="actions"
                                            onClick={
                                                detailsArrayToUse[0]?.status ===
                                                InternalUserStatus.ACTIVE
                                                    ? () =>
                                                          setDialogInView(
                                                              "deactivate-user"
                                                          )
                                                    : () =>
                                                          setDialogInView(
                                                              "activate-user"
                                                          )
                                            }
                                        >
                                            {detailsArrayToUse[0]?.status ===
                                            InternalUserStatus.ACTIVE
                                                ? "Deactivate user"
                                                : "Activate user"}
                                        </Button>
                                    )}

                                {canModifySelectedRoles && (
                                    <Button
                                        variant="secondary"
                                        aria-label="actions"
                                        onClick={() =>
                                            setDialogInView("reset-password")
                                        }
                                    >
                                        Reset Password
                                    </Button>
                                )}

                                {canModifySelected && (
                                    <Button
                                        className="text-white bg-danger hover:bg-danger hover:brightness-90"
                                        aria-label="actions"
                                        onClick={() =>
                                            setDialogInView("delete-user")
                                        }
                                    >
                                        Delete
                                    </Button>
                                )}
                            </div>
                        )}
                        {selectedUsersId.length > 0 && (
                            <div className="flex items-center gap-x-12 mb-24 sm:hidden">
                                <div className="text-base font-medium">
                                    {`${selectedUsersId.length}  selected`} :
                                </div>
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <Button className="gap-x-8 items-center">
                                            Actions
                                            <ArrowDownIcon
                                                className="w-12"
                                                fill="#ffffff"
                                            />
                                        </Button>
                                    </DropdownTrigger>
                                    <DropdownContent width="auto" align="end">
                                        {canModifySelectedRoles && (
                                            <DropdownItem
                                                onClick={() =>
                                                    setDialogInView(
                                                        "modify-user"
                                                    )
                                                }
                                            >
                                                Modify user role
                                            </DropdownItem>
                                        )}
                                        {canModifySelectedRoles && (
                                            <DropdownItem
                                                onClick={() =>
                                                    setDialogInView(
                                                        "reset-password"
                                                    )
                                                }
                                            >
                                                Reset user password
                                            </DropdownItem>
                                        )}
                                        {canManageSelectedStatus && (
                                            <DropdownItem
                                                onClick={
                                                    detailsArrayToUse[0]
                                                        ?.status ===
                                                    InternalUserStatus.ACTIVE
                                                        ? () =>
                                                              setDialogInView(
                                                                  "deactivate-user"
                                                              )
                                                        : () =>
                                                              setDialogInView(
                                                                  "activate-user"
                                                              )
                                                }
                                            >
                                                {detailsArrayToUse[0]
                                                    ?.status ===
                                                InternalUserStatus.ACTIVE
                                                    ? "Deactivate user"
                                                    : "Activate user"}
                                            </DropdownItem>
                                        )}
                                        {canModifySelected && (
                                            <DropdownItem
                                                className="text-danger"
                                                onClick={() =>
                                                    setDialogInView(
                                                        "delete-user"
                                                    )
                                                }
                                            >
                                                Delete user
                                            </DropdownItem>
                                        )}
                                    </DropdownContent>
                                </Dropdown>
                            </div>
                        )}
                        <div>
                            <Table
                                data={data.data}
                                rowSelection={rowSelection}
                                setRowSelection={setRowSelection}
                                columns={columns}
                                setGetSelectedData={setGetSelectedData}
                                setSortConfig={setSortConfig}
                                sortConfig={sortConfig}
                                sortableColumns={sortableColumns}
                                hasPagination={
                                    data.total_count > LISTS_DEFAULT_LIMIT
                                }
                                pagination={
                                    <Pagination
                                        totalCount={data.total_count}
                                        totalPages={data.total_pages}
                                        currentPage={pageFilter}
                                        onPageChange={handlePageChange}
                                        limit={limitFilter}
                                        onLimitChange={handleLimitChange}
                                    />
                                }
                            />
                        </div>
                        <div className="hidden" role="table">
                            {data.data.map((user) => (
                                <MobileListItem
                                    key={user.user_id}
                                    isSelected={selectedUsersId?.includes(
                                        user.user_id
                                    )}
                                    className="mb-5"
                                    showCheckButton={
                                        loggedInUser.data?.user_id !==
                                        user.user_id
                                    }
                                    onClickCheckButton={() =>
                                        toggleUserSelection(user.user_id)
                                    }
                                >
                                    <span className="flex items-center text-left">
                                        <span className="font-medium text-base mr-10">
                                            {`${user.first_name} ${user.last_name}`}
                                        </span>
                                        <span className="whitespace-nowrap">
                                            {getStatusDisplayForMobile(
                                                user.status
                                            )}
                                        </span>
                                    </span>
                                    <p className="text-sm font-medium text-gray my-[1.45rem] opacity-80">
                                        {user.email}
                                    </p>
                                    <p className="text-sm font-medium text-gray my-[1.45rem] opacity-80 capitalize">
                                        {user.roles
                                            .map(
                                                (role) =>
                                                    roleLabels[
                                                        role as UserRole
                                                    ] ?? role
                                            )
                                            .join(", ")}
                                    </p>
                                </MobileListItem>
                            ))}

                            {data.total_pages > 1 && (
                                <div className="fixed bottom-32 w-[calc(100%_-_38px)] sm:hidden">
                                    <Pagination
                                        totalCount={data.total_count}
                                        totalPages={data.total_pages}
                                        currentPage={pageFilter}
                                        onPageChange={handlePageChange}
                                        limit={limitFilter}
                                        onLimitChange={handleLimitChange}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
